import { useFootnoteReference } from '@dh-io-emh/footnotes/src/utils/footnote-reference.helper';
import { FootnoteGroup, FootnoteReferenceGroup } from '@dh-io-pls/price-display/dist/types';
import {
  addFootnoteByScopeSubscription,
  createFootnote,
  deleteFootnote,
  GlobalFootnote,
  GlobalFootnoteRecord,
} from '@dh-io-aem/footnote-manager';

type ScopedState = Record<GlobalFootnoteRecord['content'], GlobalFootnoteRecord>;

const prepareInput = (
  footnoteGroups: FootnoteGroup[],
  reference: string,
): { key: GlobalFootnote['content']; payload: GlobalFootnote }[] =>
  footnoteGroups
    .map((group) => group.items)
    .flat()
    .map((text) => ({ key: text, payload: { type: 'global', content: text, reference } }));

const produceScopedState = (footnoteRecords: GlobalFootnoteRecord[]): ScopedState =>
  footnoteRecords.reduce((result, currentRecord) => {
    if (!currentRecord || currentRecord.content in result) {
      return result;
    }

    result[currentRecord.content] = currentRecord as GlobalFootnoteRecord;

    return result;
  }, {} as ScopedState);

const getFootnoteIds = (scopedState: ScopedState) =>
  Object.keys(scopedState)
    .map((key) => {
      return scopedState[key].id;
    })
    .sort((a, b) => b.localeCompare(a));

const updateScopedState = (scopedState: ScopedState, footnotes: GlobalFootnoteRecord[]): [ScopedState, boolean] => {
  let shouldUpdate = false;
  const newScopedState = footnotes.reduce((result, current) => {
    const stateRecord = scopedState[current.content];

    shouldUpdate = !!stateRecord && current.index !== stateRecord.index;

    // Only set value if footnote record key is defined previously
    if (stateRecord) {
      result[current.content] = current;
    }

    return result;
  }, {} as ScopedState);

  return [newScopedState, shouldUpdate];
};

const useOWCPriceDisplayFootnotes = async (
  baseFootnoteGroups: FootnoteGroup[],
  onFootnoteReferencesUpdate: (footnoteReferenceGroups: FootnoteReferenceGroup[]) => void,
) => {
  const { generateReference } = useFootnoteReference('campaignItem');
  const reference = generateReference();

  const footnotesInput = prepareInput(baseFootnoteGroups, reference);

  // Create footnotes records and produce footnote result with the related footnote input text
  const footnotesResult = await Promise.all(
    footnotesInput.map(({ key, payload }) =>
      createFootnote(payload).then((result) => ({ key, result: result as GlobalFootnoteRecord })),
    ),
  );

  // Update footnoteGroups in order to use correct footnote text based on
  // footnote manager response
  const footnoteGroups = baseFootnoteGroups.map((group) => {
    return {
      type: group.type,
      items: group.items.map((groupItem) => footnotesResult.find((f) => f.key === groupItem)?.result.content || ''),
    };
  });

  // Maintain a local state in order to keep up to date the footnotes indexes when change
  // has been emitted from store
  let scopedState = produceScopedState(footnotesResult.map((footnote) => footnote.result));
  let initialized = false;

  const [subscription] = addFootnoteByScopeSubscription('global', (foundFootnotes) => {
    // Update scopedState if current footnotes references indexes have changed
    const [newScopedState, shouldUpdate] = updateScopedState(scopedState, foundFootnotes);

    scopedState = newScopedState;

    const groupsReferences = footnoteGroups.map((group) => {
      return {
        type: group.type,
        items: group.items.map((groupItem) => Number(scopedState[groupItem]?.index || 0)),
      };
    });

    if (!initialized || shouldUpdate) {
      onFootnoteReferencesUpdate(groupsReferences);
      initialized = true;
    }
  });

  return {
    cleanupFootnotes: async () => {
      subscription?.unsubscribe();
      await Promise.all(
        getFootnoteIds(scopedState).map((footnoteId) => {
          return deleteFootnote({ footnoteId, reference });
        }),
      );
    },
  };
};

export const usePriceDisplayFootnotes = async (
  footnoteGroups: FootnoteGroup[],
  onFootnoteReferencesUpdate: (footnoteReferenceGroups: FootnoteReferenceGroup[]) => void,
) => {
  return useOWCPriceDisplayFootnotes(footnoteGroups, onFootnoteReferencesUpdate);
};
